// Shared presentation helpers for the activity journal and the client history.
// One place so a status always looks the same wherever it's shown.

// Map a display_status to a clear, colour-coded pill. Colour = meaning.
export function getActivityPill(displayStatus, actionType) {
  const ds = displayStatus || '';
  const isCall = actionType === 'ai_call';

  if (ds === 'queued') {
    return { label: 'En file', emoji: '⏳', dot: 'bg-blue-500', text: 'text-blue-600 dark:text-blue-400' };
  }
  if (ds === 'call_sent') {
    return { label: 'Appel lancé', emoji: '📞', dot: 'bg-green-500', text: 'text-green-600 dark:text-green-400' };
  }
  if (ds === 'sms_skipped_landline') {
    return { label: 'Ignoré · fixe', emoji: '⚠️', dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' };
  }
  if (ds === 'failed' || ds === 'call_failed' || ds === 'error') {
    return { label: 'Échec', emoji: '❌', dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400' };
  }
  if (ds === 'success' || ds === 'sent' || ds === 'delivered') {
    return {
      label: isCall ? 'Appel lancé' : 'Envoyé', emoji: isCall ? '📞' : '✅',
      dot: 'bg-green-500', text: 'text-green-600 dark:text-green-400',
    };
  }
  // Unknown label — neutral, never crash.
  return { label: ds || 'Inconnu', emoji: '•', dot: 'bg-muted-foreground/50', text: 'text-muted-foreground' };
}

// Map an email engagement result to a pill. Null when there's nothing to show
// (calls/SMS, or an email with no event received yet → the cell shows "—").
// Note: "Ouvert" is indicative (Apple Mail auto-loads the tracker); "Cliqué"
// is the trustworthy signal.
export function getResultPill(result) {
  switch (result) {
    case 'clicked':
      return { label: 'Cliqué', emoji: '👆', dot: 'bg-green-500', text: 'text-green-600 dark:text-green-400' };
    case 'opened':
      return { label: 'Ouvert', emoji: '👁', dot: 'bg-teal-500', text: 'text-teal-600 dark:text-teal-400' };
    case 'delivered':
      return { label: 'Délivré', emoji: '📬', dot: 'bg-blue-500', text: 'text-blue-600 dark:text-blue-400' };
    case 'bounced':
      return { label: 'Rejeté', emoji: '⛔', dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400' };
    case 'complained':
      return { label: 'Plainte spam', emoji: '🚫', dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400' };
    case 'sent':
      return { label: 'Envoyé', emoji: '✅', dot: 'bg-muted-foreground/50', text: 'text-muted-foreground' };
    default:
      return null;
  }
}

// Pastille courte "Résultat" pour une ligne d'APPEL (colonne du journal), dérivée
// de la réaction du webhook. null si pas encore de résultat (appel juste lancé).
export function getCallResultPill(reaction) {
  const green = { dot: 'bg-green-500', text: 'text-green-600 dark:text-green-400' };
  const amber = { dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' };
  const blue = { dot: 'bg-blue-500', text: 'text-blue-600 dark:text-blue-400' };
  const grey = { dot: 'bg-muted-foreground/50', text: 'text-muted-foreground' };
  switch (reaction) {
    case 'google_review_email':
    case 'google_review_sms': return { label: 'Avis Google envoyé', ...green };
    case 'callback_queued_interesse_bilan':
    case 'callback_queued_interesse_reactivation': return { label: 'Intéressé', ...green };
    case 'callback_queued_a_verifier': return { label: 'À vérifier', ...blue };
    case 'callback_queued_rappel':
    case 'callback_queued_unknown': return { label: 'Mécontent', ...amber };
    case 'retry_exhausted_callback_queued': return { label: 'Injoignable', ...amber };
    case 'none_pas_interesse': return { label: 'Pas intéressé', ...grey };
    default:
      if (reaction && (reaction.startsWith('retry_scheduled') || reaction.startsWith('recorded_')))
        return { label: 'Sans réponse', ...grey };
      return null;
  }
}

// Traduit la "suite donnée" après un appel (le champ `reaction` = action du
// webhook Callkom) en libellé lisible. Renvoie null pour le bruit technique
// (doublons, webhooks sans analyse) qu'on ne montre pas.
export function getReactionLabel(reaction) {
  switch (reaction) {
    case 'google_review_email': return 'Lien avis Google envoyé par email';
    case 'google_review_sms': return 'Lien avis Google envoyé par SMS';
    case 'callback_queued_interesse_bilan': return 'Intéressé (Bilan) — rappel programmé + lien envoyé';
    case 'callback_queued_interesse_reactivation': return 'Intéressé (Réactivation) — rappel programmé + lien envoyé';
    case 'callback_queued_a_verifier': return 'À vérifier — ajouté à « À rappeler »';
    case 'callback_queued_rappel': return 'Mécontent — rappel programmé + cabinet alerté';
    case 'callback_queued_unknown': return 'À clarifier — ajouté à « À rappeler »';
    case 'none_pas_interesse': return 'Pas intéressé — aucune action';
    case 'retry_exhausted_callback_queued': return 'Injoignable — ajouté à « À rappeler »';
    case 'tech_failure_alerted': return 'Échec technique — cabinet alerté';
    default:
      if (reaction && reaction.startsWith('retry_scheduled')) return 'Sans réponse — rappel automatique programmé';
      if (reaction && reaction.startsWith('recorded_')) return 'Sans réponse (répondeur / pas décroché)';
      return null;  // ignored_no_analysis, duplicate_skipped, none… → non affiché
  }
}

// Action type → French label + emoji icon.
export function getActionMeta(actionType) {
  switch (actionType) {
    case 'ai_call': return { label: 'Appel', emoji: '📞' };
    case 'email': return { label: 'Email', emoji: '✉️' };
    case 'sms': return { label: 'SMS', emoji: '💬' };
    default: return { label: actionType || '—', emoji: '•' };
  }
}

// Short, readable French date: "12/07 14:30".
export function formatShortDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}
